<template>
    <div class="text-search">
        <SearchSuggest
            ref="searchSuggestRes"
            :model-value="search"
            class="text-search__search"
            :options="searchOptions"
            :hide-dropdown="hideDropdown"
            :placeholder="$t('textsearch.placeholder')"
            type="search"
            @update:model-value="onSearchInput"
            @selected="onSearchSuggested"
            @below-min-symbols="clearMatches"
        >
            <template v-if="!noShowAllLink" #firstline>
                <button
                    v-t="'textsearch.showAll'"
                    class="button button--blank button--fullwidth"
                    type="button"
                    style="text-align: left"
                    @click="goToSearchPage"
                />
            </template>
            <template v-slot:default="{ item, makeOptionText }">
                <span :title="item.raw_text" v-html="makeOptionText(item.raw_text)"></span>
            </template>
        </SearchSuggest>
    </div>
</template>

<script>
import SearchSuggest from '@speechki-frontend/shared/ui/SearchSuggest';

import { searchText } from '@speechki-frontend/core/book/api/books';
import { expandMatches } from './searchMatches';

export default {
    name: 'TextSearch',
    components: {
        SearchSuggest,
    },
    props: {
        book: {
            type: Number,
            required: true,
        },
        modelValue: {
            type: String,
            value: '',
        },
        noShowAllLink: {
            type: Boolean,
            default: false,
        },
        // Pass-through to SearchSuggest's hideDropdown — used by the
        // Professional Prooflistening top panel (EditorText.vue) for its
        // Ctrl+F-style "type + cycle matches" flow, with a separate match
        // list in its top panel. Default false keeps every other TextSearch usage
        // (including the default .top-panel branch in EditorText.vue)
        // showing its dropdown exactly as today.
        hideDropdown: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'select', 'matchesChanged'],
    data() {
        return {
            search: '',
            searchOptions: [],
            resultQuery: '',
            // Tracks which occurrence "prev/next match" navigation
            // is currently on — used only by the Professional Prooflistening
            // top-panel's match-count/cycle buttons (see EditorText.vue);
            // the default dropdown-select flow below doesn't read this.
            activeMatchIndex: -1,
            // Incremented per search/clear so late responses for an old query are dropped.
            searchRequestId: 0,
        };
    },
    inject: ['isEmbed'],
    computed: {
        matches() {
            return expandMatches(this.searchOptions, this.resultQuery);
        },
    },
    methods: {
        async onSearchInput(search) {
            if (!this.book) return;
            this.search = search;
            this.$emit('update:modelValue', search);
            if (search.trim().length < 3) {
                this.clearMatches(search);
                return;
            }
            const requestId = ++this.searchRequestId;
            try {
                const options = await searchText(this.book, search);
                // Ignore a response that arrives after the query changed or was cleared.
                if (requestId !== this.searchRequestId) return;
                this.searchOptions = Object.freeze(options);
                this.resultQuery = search.trim();
                this.activeMatchIndex = -1;
                this.emitMatchesChanged();
            } catch (error) {
                console.log(error);
            }
        },
        // Called when the input is emptied or shortened below SearchSuggest's
        // minSymbols: the previous results no longer match what is typed.
        clearMatches(search = '') {
            this.searchRequestId++;
            this.search = search;
            this.resultQuery = '';
            this.searchOptions = [];
            this.activeMatchIndex = -1;
            this.emitMatchesChanged();
        },
        onSearchSuggested(event) {
            // Only drop results here. SearchSuggest.select() clears its own input,
            // which then emits an empty query.
            this.resultQuery = '';
            this.searchOptions = [];
            this.activeMatchIndex = -1;
            this.emitMatchesChanged();
            this.$emit('select', event);
        },
        emitMatchesChanged() {
            this.$emit('matchesChanged', {
                count: this.matches.length,
                index: this.activeMatchIndex,
                matches: this.matches,
                query: this.resultQuery,
            });
        },
        // Steps to the next/previous fetched match and navigates to it —
        // reuses the same navigation emit as picking a dropdown option
        // (`$emit('select', event)`), but deliberately does NOT go through
        // onSearchSuggested: that method clears searchOptions/activeMatchIndex
        // for the "pick one result and be done" dropdown-click flow, which
        // would make repeated prev/next clicks stop working after the first
        // one (the list they cycle through would already be empty). Cycling
        // keeps the list alive and just moves the pointer within it. A new query
        // replaces results only after its response arrives. Note:
        // the SearchSuggest clear (X) button is hidden via
        // `.search-suggest__right { display: none }` in TextSearch.scss for
        // the reviewer field, so it can't be used to clear the list there.
        goToMatch(step) {
            if (!this.matches.length) return;

            const total = this.matches.length;
            let nextIndex;
            if (this.activeMatchIndex === -1) {
                nextIndex = step > 0 ? 0 : total - 1;
            } else {
                nextIndex = (((this.activeMatchIndex + step) % total) + total) % total;
            }
            this.goToMatchIndex(nextIndex);
        },
        goToMatchIndex(index) {
            if (!Number.isInteger(index) || !this.matches[index]) return;
            this.activeMatchIndex = index;
            this.emitMatchesChanged();
            this.$emit('select', this.matches[index].option);
        },
        goToSearchPage() {
            this.$router.push({
                name: this.isEmbed ? 'embed-search' : 'search',
                params: {
                    id: this.$route.params.id,
                },
                query: {
                    search: this.search,
                },
            });
        },
        setSearch(value) {
            this.onSearchInput(value);
        },
    },
};
</script>

<style lang="scss" src="./TextSearch.scss"></style>
